/**
 * **알림 설정의 빠른 선택(매일 · 매주 · 매월 · 실시간)과 화면 대상** — 어느 저장 형식에 무엇을 적나.
 *
 * ```
 *   대상                   빠른 선택이 적히는 곳                                          실시간이 켜지는 조건
 *   브랜드 멘션 추적        워크스페이스 messageSettings.brandMention.{messageEmail,       워크스페이스 Slack 웹훅
 *                         messageEmailWeekly, messageEmailMonthly, messageSlackRealTime}
 *   타겟 멘션 추적 세트     세트 문서의 같은 이름 칸                                         그 세트의 Slack 웹훅
 *   시딩 캠페인            빠른 선택 없음 — 창이 처음부터 맞춤 편집기로 열린다(`hasQuickAlerts`)
 * ```
 *
 * 🔑 앞의 둘은 **기본 알림**(추적 관리 🔔 가 여는 창)과 같은 칸이다 — 새 규칙을 만들면
 *   이미 켜진 기본 알림과 겹쳐 같은 메일이 두 번 간다.
 * 🚨 시딩 캠페인의 알림은 **늘 맞춤**이다(사용자 결정) — 빠른 선택 카드를 세우지 않는다.
 *   조건 없는 규칙으로 옮기는 어댑터(`readRuleQuickAlerts` · `planRuleQuickChange` · `buildQuickAlertDraft`)는 계약으로 남아 있고 화면은 부르지 않는다.
 *   규칙의 주기에는 매월이 없다(`AlertScheduleMode`) — 어댑터는 매월을 다루지 않고 늘 꺼짐으로 읽는다.
 * ⚠️ 기본 알림에는 채널·국가 필터 칸이 없다 — 화면의 필터는 「맞춤으로 만들기」의 초안에 싣는다
 *   (`toAlertRuleFilterFromScreen`).
 * ⚠️ 순수 함수다 — `'use client'` 를 붙이지 않는다.
 */

import type { TrackAlerts } from 'src/utils/workspace/track-register';
import type {
  AlertRuleDraft,
  QuickAlertMode,
  AlertRuleFilter,
  QuickAlertState,
  RuleQuickAlertMode,
  AlertSettingTarget,
} from 'src/types/alert-rule/alert-rule-domain';

import { getMainPlatforms } from 'src/utils/brand/brand-filter-utils';
import { toFilterCodes } from 'src/utils/moments/view/moment-filter-bar-labels';

import { ALERT_SOURCE_TYPES, ALERT_MONTHLY_VISIBLE, ALERT_CAMPAIGN_COLLECTIONS } from 'src/constants/alert-rule';

import { alertSourceLabel } from './alert-rule-summary';
import {
  isAlertSourceReady,
  resolveSlackWebhookUrl,
  normalizeAlertConditions,
  normalizeAlertRuleFilter,
  createEmptyAlertRuleDraft,
  normalizeSlackConnectionPayload,
  normalizeAlertContentForSchedule,
} from './alert-rule-utils';

/** 빠른 선택이 적히는 곳 — 머리말 표 */
export type QuickAlertStore = 'brandMention' | 'keywordSet' | 'alertRule';

/** 빠른 선택 카드 넷 — 차례가 곧 화면 차례다 */
export const QUICK_ALERT_OPTIONS: { mode: QuickAlertMode; label: string; description: string; icon: string }[] = [
  { mode: 'daily', label: '매일', description: '하루 한 번 이메일로 받습니다', icon: 'solar:calendar-linear' },
  { mode: 'weekly', label: '매주', description: '한 주에 한 번 이메일로 받습니다', icon: 'solar:calendar-mark-linear' },
  { mode: 'monthly', label: '매월', description: '한 달에 한 번 이메일로 받습니다', icon: 'solar:calendar-date-linear' },
  { mode: 'realtime', label: '실시간', description: '새 글이 잡히면 Slack 으로 바로 받습니다', icon: 'solar:bolt-linear' },
];

/**
 * 화면에 세우는 빠른 선택 카드 — 「보류」 매월은 `ALERT_MONTHLY_VISIBLE` 이 꺼져 있으면 빠진다(저장 칸은 그대로).
 * @param monthlyVisible 매월 카드를 세우나 — 기본은 스위치 값
 */
export function visibleQuickAlertOptions(monthlyVisible: boolean = ALERT_MONTHLY_VISIBLE): typeof QUICK_ALERT_OPTIONS {
  return monthlyVisible ? QUICK_ALERT_OPTIONS : QUICK_ALERT_OPTIONS.filter((option) => option.mode !== 'monthly');
}

/** 넓은 화면의 카드 칸 수 — 넷이면 두 줄 두 칸, 셋 이하면 한 줄에 나란히 */
export function quickAlertColumns(count: number): number {
  return count === 4 ? 2 : Math.max(1, count);
}

/** 실시간 카드가 잠겼을 때의 한 줄 — 다음 할 일을 적는다 */
export const QUICK_ALERT_SLACK_NEEDED = 'Slack 을 연결하면 켤 수 있어요';

/** 기본 알림의 칸 이름 — 빠른 선택 하나가 칸 하나다 */
export const QUICK_ALERT_BASIC_FIELD: Record<QuickAlertMode, string> = {
  daily: 'messageEmail',
  weekly: 'messageEmailWeekly',
  monthly: 'messageEmailMonthly',
  realtime: 'messageSlackRealTime',
};

/** 화면 대상 → 저장 문서의 `source` */
export function alertTargetToSource(target: AlertSettingTarget): AlertRuleDraft['source'] {
  if (target.kind === 'keywordMonitoring') {
    return {
      type: 'keyword_monitoring',
      campaignId: target.campaignId,
      campaignCollection: ALERT_CAMPAIGN_COLLECTIONS.KEYWORD_MONITORING,
    };
  }
  if (target.kind === 'seedingCampaign') {
    return {
      type: 'seeding_campaign',
      campaignId: target.campaignId,
      campaignCollection: ALERT_CAMPAIGN_COLLECTIONS.SEEDING_CAMPAIGN,
    };
  }
  return { type: 'brand_mention', campaignId: '', campaignCollection: '' };
}

/** 저장된 규칙 → 화면 대상. 모르는 대상이면 `null` */
export function ruleToAlertTarget(rule: Record<string, any> | null | undefined): AlertSettingTarget | null {
  const type = String(rule?.source?.type ?? '');
  const campaignId = String(rule?.source?.campaignId ?? '').trim();
  if (type === ALERT_SOURCE_TYPES.BRAND_MENTION) return { kind: 'brandMention' };
  if (type === ALERT_SOURCE_TYPES.KEYWORD_MONITORING && campaignId) return { kind: 'keywordMonitoring', campaignId };
  if (type === ALERT_SOURCE_TYPES.SEEDING_CAMPAIGN && campaignId) return { kind: 'seedingCampaign', campaignId };
  return null;
}

/** 대상을 한 글자로 — 고르개 값 · 키 */
export function alertTargetKey(target: AlertSettingTarget): string {
  return target.kind === 'brandMention' ? target.kind : `${target.kind}:${target.campaignId}`;
}

/** 이 대상에 걸린 규칙인가 */
export function isRuleOfTarget(rule: Record<string, any> | null | undefined, target: AlertSettingTarget): boolean {
  const source = alertTargetToSource(target);
  if (String(rule?.source?.type ?? '') !== source.type) return false;
  if (!source.campaignId) return true;
  return String(rule?.source?.campaignId ?? '') === source.campaignId;
}

/** 이 대상의 창에 빠른 선택(매일·매주·매월·실시간)을 세우나 — 시딩 캠페인은 늘 맞춤이라 세우지 않는다 */
export function hasQuickAlerts(target: AlertSettingTarget): boolean {
  return target.kind !== 'seedingCampaign';
}

/** 빠른 선택이 어디에 적히나 */
export function quickAlertStore(target: AlertSettingTarget): QuickAlertStore {
  if (target.kind === 'brandMention') return 'brandMention';
  if (target.kind === 'keywordMonitoring') return 'keywordSet';
  return 'alertRule';
}

/** 대상의 캠페인 문서 — 세트 · 시딩 캠페인. 브랜드 멘션이면 `null` */
export function findTargetCampaign(
  target: AlertSettingTarget,
  lists: { keywordCampaigns?: Record<string, any>[]; seedingCampaigns?: Record<string, any>[] },
): Record<string, any> | null {
  if (target.kind === 'brandMention') return null;
  const list = target.kind === 'keywordMonitoring' ? lists.keywordCampaigns : lists.seedingCampaigns;
  return (Array.isArray(list) ? list : []).find((row) => String(row?.id ?? '') === target.campaignId) ?? null;
}

/**
 * 대상의 이름 — 「브랜드 멘션 추적」 · 「타겟 멘션 추적 · {세트}」 · 「시딩 캠페인 · {캠페인}」.
 * 캠페인 이름을 모르면 종류 이름만 적는다.
 */
export function alertTargetLabel(
  target: AlertSettingTarget,
  lists: { keywordCampaigns?: Record<string, any>[]; seedingCampaigns?: Record<string, any>[] },
): string {
  const name = String(findTargetCampaign(target, lists)?.name ?? '').trim();
  return alertSourceLabel({ source: alertTargetToSource(target) }, name);
}

/** 워크스페이스·캠페인 문서에 Slack 웹훅이 있나 — 실시간이 갈 곳 */
export function hasSlackWebhook(doc: Record<string, any> | null | undefined): boolean {
  return Boolean(resolveSlackWebhookUrl(doc?.slack, doc?.slackWebhookUrl ?? doc?.slackUrl));
}

/**
 * 실시간 카드를 켤 수 있나 — 기본 알림의 발송기는 웹훅이 없으면 건너뛴다.
 * 세트는 **그 세트의** 웹훅을 본다(세트마다 다른 채널로 보낸다).
 */
export function isQuickRealtimeReady(
  target: AlertSettingTarget,
  ctx: { workspace?: Record<string, any> | null; keywordCampaigns?: Record<string, any>[] },
): boolean {
  if (target.kind === 'keywordMonitoring') return hasSlackWebhook(findTargetCampaign(target, ctx));
  return hasSlackWebhook(ctx.workspace);
}

/**
 * 기본 알림 칸 → 켜짐 넷 — 발송기와 같은 판정이다. 실시간은 웹훅이 있을 때만 켬으로 읽는다.
 * ⚠️ 매일 이메일만 저장 형식마다 다르다 — 브랜드 멘션은 `false` 만 끔(저장값이 없으면 켬) ·
 *   세트는 `true` 만 켬(세트 발송기가 `=== true` 일 때만 보낸다 — 저장값이 없으면 끔).
 * @param store 칸이 놓인 곳 — 워크스페이스 브랜드 멘션 · 세트 문서
 */
export function readBasicQuickAlerts(
  fields:
    | { messageEmail?: unknown; messageEmailWeekly?: unknown; messageEmailMonthly?: unknown; messageSlackRealTime?: unknown }
    | null
    | undefined,
  realtimeReady: boolean,
  store: Exclude<QuickAlertStore, 'alertRule'>,
): QuickAlertState {
  return {
    daily: store === 'keywordSet' ? fields?.messageEmail === true : fields?.messageEmail !== false,
    weekly: fields?.messageEmailWeekly === true,
    monthly: fields?.messageEmailMonthly === true,
    realtime: realtimeReady && fields?.messageSlackRealTime === true,
  };
}

/**
 * 화면에 적는 켜짐 넷 — 「보류」 매월은 `ALERT_MONTHLY_VISIBLE` 이 꺼져 있으면 꺼짐으로 적는다.
 * 목록 줄 · 🔔 칩 · 대시보드 칩 · 홈 「알림 켜기」처럼 **읽기만 하는** 화면이 모두 이 함수를 거친다.
 * ⛔ 저장에 넘기지 않는다 — 다른 주기를 저장할 때 이 값을 쓰면 저장된 `messageEmailMonthly` 가 지워진다.
 * @param monthlyVisible 매월을 화면에 적나 — 기본은 스위치 값
 */
export function visibleQuickAlertState(state: QuickAlertState, monthlyVisible: boolean = ALERT_MONTHLY_VISIBLE): QuickAlertState {
  return monthlyVisible ? state : { ...state, monthly: false };
}

/** 빠른 선택 넷이 다 꺼짐 */
export const QUICK_ALERTS_OFF: QuickAlertState = { daily: false, weekly: false, monthly: false, realtime: false };

/**
 * 대상 하나의 기본 알림 켜짐 넷 — 알림 설정 창 · 설정 「알림」 목록 · 추적 관리 🔔 가 같은 판정을 읽는다.
 * 칸은 대상의 저장 자리(`quickAlertStore`)에서, 실시간 준비는 `isQuickRealtimeReady`(세트는 그 세트의 웹훅)에서 온다.
 * @returns 시딩 캠페인은 빠른 선택이 없어 늘 다 꺼짐
 */
export function readTargetQuickAlerts(
  target: AlertSettingTarget,
  ctx: { workspace?: Record<string, any> | null; keywordCampaigns?: Record<string, any>[] },
): QuickAlertState {
  if (!hasQuickAlerts(target)) return QUICK_ALERTS_OFF;
  const realtimeReady = isQuickRealtimeReady(target, ctx);
  if (quickAlertStore(target) === 'brandMention') {
    return readBasicQuickAlerts(ctx.workspace?.messageSettings?.brandMention, realtimeReady, 'brandMention');
  }
  return readBasicQuickAlerts(findTargetCampaign(target, ctx), realtimeReady, 'keywordSet');
}

/** 이 대상에 걸린 **켜진** 맞춤 알림 수 — 꺼진 규칙은 보내지 않으므로 세지 않는다 */
export function countEnabledTargetRules(rules: Record<string, any>[] | null | undefined, target: AlertSettingTarget): number {
  return (Array.isArray(rules) ? rules : []).filter((rule) => rule?.enabled === true && isRuleOfTarget(rule, target)).length;
}

/** 켜짐 넷 → 추적 등록의 알림 칸(`toBrandMentionAlertPatch` 가 받는 모양) */
export function toTrackAlerts(state: QuickAlertState): TrackAlerts {
  return { daily: state.daily, weekly: state.weekly, monthly: state.monthly, slack: state.realtime };
}

/** 빠른 선택이 만든 규칙의 모양인가 — 조건도 채널·국가 필터도 없다 */
export function isQuickAlertRule(rule: Record<string, any> | null | undefined): boolean {
  if (!rule) return false;
  const filter = normalizeAlertRuleFilter(rule.filter);
  return normalizeAlertConditions(rule).length === 0 && filter.platforms.length === 0 && filter.markets.length === 0;
}

/** 규칙 목록(이 대상의 것) → 켜짐 넷 — 그 주기의 빠른 규칙이 하나라도 켜져 있으면 켬 · 매월은 늘 꺼짐 */
export function readRuleQuickAlerts(rules: Record<string, any>[]): QuickAlertState {
  const on = (mode: RuleQuickAlertMode) =>
    rules.some((rule) => isQuickAlertRule(rule) && rule?.enabled === true && String(rule?.schedule?.mode ?? '') === mode);
  return { daily: on('daily'), weekly: on('weekly'), monthly: false, realtime: on('realtime') };
}

/**
 * 규칙으로 적는 대상에서 카드 하나를 바꿀 때 할 일.
 *
 * - 켜기: 그 주기의 빠른 규칙이 켜져 있으면 할 일 없음 → 꺼진 것이 있으면 그것을 켠다 → 없으면 새로 만든다.
 * - 끄기: 그 주기의 켜진 빠른 규칙을 모두 끈다(지우지 않는다 — 목록에서 다시 켤 수 있다).
 * ⚠️ 조건·필터가 걸린 맞춤 규칙은 건드리지 않는다.
 */
export function planRuleQuickChange(
  rules: Record<string, any>[],
  mode: RuleQuickAlertMode,
  on: boolean,
): { enable: string[]; disable: string[]; create: boolean } {
  const quick = rules.filter((rule) => isQuickAlertRule(rule) && String(rule?.schedule?.mode ?? '') === mode);
  const enabled = quick.filter((rule) => rule?.enabled === true).map((rule) => String(rule?.id ?? ''));
  if (!on) return { enable: [], disable: enabled.filter(Boolean), create: false };
  if (enabled.length > 0) return { enable: [], disable: [], create: false };
  const sleeping = String(quick[0]?.id ?? '');
  return sleeping ? { enable: [sleeping], disable: [], create: false } : { enable: [], disable: [], create: true };
}

/**
 * 빠른 선택이 새로 만드는 규칙의 초안 — 대상 · 주기만 정하고 나머지는 기본값이다.
 *
 * 매일·매주 = 이메일(멤버 전원) · 09시 · 매주는 월요일 / 실시간 = Slack(워크스페이스 채널) · 글 목록만.
 * @param targetLabel 이름 앞머리 — 「시딩 캠페인 · 봄 시딩」
 */
export function buildQuickAlertDraft({
  target,
  mode,
  targetLabel,
  memberIds,
  workspace,
}: {
  target: AlertSettingTarget;
  mode: RuleQuickAlertMode;
  targetLabel: string;
  memberIds: string[];
  workspace: Record<string, any> | null | undefined;
}): AlertRuleDraft {
  const draft = createEmptyAlertRuleDraft();
  const label = QUICK_ALERT_OPTIONS.find((row) => row.mode === mode)?.label ?? mode;
  const realtime = mode === 'realtime';
  const slack = realtime ? normalizeSlackConnectionPayload(workspace?.slack, workspace?.slackUrl) : null;
  return {
    ...draft,
    name: `${targetLabel} ${label} 알림`.trim(),
    source: alertTargetToSource(target),
    schedule: { ...draft.schedule, mode },
    channels: {
      email: { enabled: !realtime, recipientUserIds: realtime ? [] : [...memberIds] },
      slack: { enabled: realtime },
    },
    content: normalizeAlertContentForSchedule(mode, draft.content),
    slack,
    slackWebhookUrl: slack?.webhookUrl ?? '',
  };
}

/**
 * 화면의 필터(채널 · 국가) → 알림 규칙의 필터. 고른 것이 없으면 「전체」(빈 목록)다.
 * ⚠️ 세부 채널 칩(인스타 포스트·스토리 …)은 싣지 않는다 — 알림 필터에는 큰 채널만 있다.
 */
export function toAlertRuleFilterFromScreen(
  filter: { platforms?: unknown; country?: unknown } | null | undefined,
): AlertRuleFilter {
  return normalizeAlertRuleFilter({
    platforms: getMainPlatforms(filter?.platforms),
    markets: toFilterCodes(filter?.country),
  });
}

/**
 * 고를 수 있는 대상 — 대상을 쥐지 않고 연 자리의 고르개가 읽는다(지금 대상 없이 여는 자리는 없다).
 * @param canUse 기능 열쇠(`brandManagement` …)가 플랜에서 열려 있나
 */
export function listAlertTargets({
  workspace,
  keywordCampaigns = [],
  seedingCampaigns = [],
  canUse,
}: {
  workspace?: Record<string, any> | null;
  keywordCampaigns?: Record<string, any>[];
  seedingCampaigns?: Record<string, any>[];
  canUse: (featureKey: 'brandManagement' | 'keywordMonitoring' | 'influencerCampaign') => boolean;
}): { key: string; target: AlertSettingTarget; label: string }[] {
  const lists = { keywordCampaigns, seedingCampaigns };
  const out: AlertSettingTarget[] = [];
  if (canUse('brandManagement') && isAlertSourceReady(ALERT_SOURCE_TYPES.BRAND_MENTION, { workspace })) {
    out.push({ kind: 'brandMention' });
  }
  if (canUse('keywordMonitoring')) {
    keywordCampaigns.forEach((row) => {
      const id = String(row?.id ?? '').trim();
      if (id) out.push({ kind: 'keywordMonitoring', campaignId: id });
    });
  }
  if (canUse('influencerCampaign')) {
    seedingCampaigns.forEach((row) => {
      const id = String(row?.id ?? '').trim();
      if (id) out.push({ kind: 'seedingCampaign', campaignId: id });
    });
  }
  return out.map((target) => ({ key: alertTargetKey(target), target, label: alertTargetLabel(target, lists) }));
}
